/**
 * useServiceWorkerUpdate
 * ──────────────────────
 * Détecte qu'un nouveau Service Worker est en attente (waiting) et affiche
 * un toast Sonner non-bloquant proposant de recharger pour appliquer la
 * mise à jour. Évite à l'utilisateur d'avoir à passer par
 * `/reset-cache.html` manuellement après un déploiement.
 *
 * Vérifie également périodiquement (toutes les 15 min) si une update est
 * disponible — utile pour les PWA iOS qui restent ouvertes des heures
 * sans naviguer.
 *
 * Le mécanisme `controllerchange` (déclenché par skipWaiting + claim côté
 * sw.js) est déjà géré dans `index.js` pour recharger la page après que
 * le nouveau SW a pris le contrôle.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const PERIODIC_CHECK_MS = 15 * 60 * 1000; // 15 minutes

export function useServiceWorkerUpdate() {
  const toastShownRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    let intervalId = null;

    const showUpdateToast = (waitingWorker) => {
      if (toastShownRef.current) return;
      toastShownRef.current = true;
      toast.message('Nouvelle version disponible', {
        description: "Rechargez pour appliquer les dernières mises à jour.",
        duration: Infinity,
        dismissible: true,
        action: {
          label: 'Recharger',
          onClick: () => {
            // Demande au SW en waiting d'activer immédiatement.
            // Le `controllerchange` listener de index.js fera le reload.
            try {
              if (waitingWorker && typeof waitingWorker.postMessage === 'function') {
                waitingWorker.postMessage({ type: 'SKIP_WAITING' });
              }
            } catch (e) { /* noop */ }
            // Filet de sécurité : reload après 1s même si le SW ne répond pas
            setTimeout(() => window.location.reload(), 1000);
          },
        },
      });
    };

    const checkForUpdate = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        // Demande au browser de vérifier sw.js — Promise async, à catcher
        // sinon "null/nonexistent service worker registration" remonte au
        // dev overlay React quand le SW est en état transitoire.
        await reg.update().catch(() => {});
        if (reg.waiting && navigator.serviceWorker.controller) {
          showUpdateToast(reg.waiting);
        }
      } catch (e) { /* noop */ }
    };

    // Setup au mount
    navigator.serviceWorker.ready.then((reg) => {
      // Si une update est déjà en waiting au chargement
      if (reg.waiting && navigator.serviceWorker.controller) {
        showUpdateToast(reg.waiting);
      }
      // Écoute les futures updates
      reg.addEventListener('updatefound', () => {
        const newSW = reg.installing;
        if (!newSW) return;
        newSW.addEventListener('statechange', () => {
          if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
            showUpdateToast(newSW);
          }
        });
      });
    }).catch(() => {});

    // Re-check toutes les 15 min (utile pour iOS PWA long-lived)
    intervalId = setInterval(checkForUpdate, PERIODIC_CHECK_MS);

    // Re-check quand l'onglet redevient visible (retour de background)
    const onVis = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
}

export default useServiceWorkerUpdate;
